/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  List,
  Label,
  Title,
  Text, 
  ObjectStatus, 
  FlexBox, 
  FlexBoxAlignItems, 
  FlexBoxDirection, 
  FlexBoxJustifyContent, 
  Icon,
  Button,
  Select,
  Option,
  Input,
  Dialog,
  AnalyticalTable,
  BusyIndicator,
  DatePicker
} from '@ui5/webcomponents-react';
import { parseToNumber, formatNumberWithComma, rightAlignedNumberStyle, getStatusStyle, getStatusComponentProps, formattedNumberCell, formatCurrency } from '../utils/validator';
import useAutoTranslate from '../hooks/useAutoTranslate'; // Importar hook de traducción
import usePermission from '../hooks/usePermission';
import AccessDenied from './AccessDenied';
import { sendSalesOrdersToSapAndWait } from '../services/sapB1SalesOrdersSend';

/** OV: `SalesOrderList` (órdenes de venta). Lazy para no cargar Excel/SAP de mayorista al abrir solo OC. */
const SalesOrderList = lazy(() => import('./SalesOrderList'));

export const BuyOrderList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canPurchase = hasPermission('buyorder', 'listView');
  const canSales = hasPermission('salesorder', 'listView');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [originalOrders, setOriginalOrders] = useState([]);
  const fileInputRef = useRef(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [dateFilter, setDateFilter] = useState([null, null]);
  const [creationDateFilter, setCreationDateFilter] = useState([null, null]);
  const [deliveryDateFilter, setDeliveryDateFilter] = useState([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // SAP B1 integration
  const [showSapConfirmDialog, setShowSapConfirmDialog] = useState(false);
  const [showSapResultDialog, setShowSapResultDialog] = useState(false);
  const [sapResult, setSapResult] = useState(null);
  const [sapLoading, setSapLoading] = useState(false);

  // ==========================
  // NUEVO (2026-03-17)
  // Switch OC/OV: `?view=sales` en la URL (válido en `/home` embebido y en `/buy-order-list` directo; volver desde detalle OV usa `/home?view=sales`).
  // ==========================
  const [searchParams, setSearchParams] = useSearchParams();
  const orderView = searchParams.get('view') === 'sales' ? 'sales' : 'purchase';
  const setOrderView = (mode) => {
    if (mode === 'sales') {
      setSearchParams({ view: 'sales' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Si el usuario no tiene permiso para la vista actual pero sí para la otra,
  // se conmuta automáticamente para evitar pantalla vacía / fetch inútil.
  useEffect(() => {
    if (orderView === 'purchase' && !canPurchase && canSales) {
      setOrderView('sales');
    } else if (orderView === 'sales' && !canSales && canPurchase) {
      setOrderView('purchase');
    }
  }, [orderView, canPurchase, canSales]);
  
  const [orderNumberSearch, setOrderNumberSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [startDateSearch, setStartDateSearch] = useState('');
  const [endDateSearch, setEndDateSearch] = useState('');
  const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);

  const { autoT } = useAutoTranslate(); // Usar hook de traducción

  const statusOptions = [
    { value: 'all', label: autoT('Estados') },
    { value: 'Open', label: autoT('Despacho Parcial') },
    { value: 'Approved', label: autoT('Despacho Total') },
    { value: 'Shipped', label: autoT('Aceptada') },
    { value: 'Pending', label: autoT('Liberada') },
    { value: 'Canceled', label: autoT('Rechazada') }
  ];

  const dialogStatusOptions = [
    { value: 'Shipped', label: autoT('Aceptada') },
    { value: 'Canceled', label: autoT('Rechazada') }
  ];

  const getStatusLabel = (statusValue) => {
    const status = statusOptions.find(opt => opt.value === statusValue);
    return status ? status.label : statusValue;
  };

  const fetchOrders = async (page = 1, useAdvancedSearch = isAdvancedSearchActive) => {
    setLoading(true);
    
    try {
      if (useAdvancedSearch && (orderNumberSearch || statusSearch || startDateSearch || endDateSearch)) {
        await handleAdvancedSearch(orderNumberSearch, statusSearch, startDateSearch, endDateSearch, page);
        return;
      }
      
      const url = `purchase-orders?page=${page}&size=${pageSize}`;
      console.log(autoT('Enviando solicitud a:'), axiosInstance.defaults.baseURL + url);
      
      const response = await axiosInstance.get(url);
      if (response.data.records.code === "SUCCESS") {
        setOrders(response.data.records.data);
        setOriginalOrders(response.data.records.data);
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      console.error(autoT('Error fetching data:'), err);
      setErrorMessage(autoT('Error fetching data'));
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedSearch = async (
    orderNum = orderNumberSearch, 
    status = statusSearch, 
    startDate = startDateSearch, 
    endDate = endDateSearch, 
    page = currentPage
  ) => {
    setLoading(true);
    setIsAdvancedSearchActive(true);
    
    try {
      console.log('=== handleAdvancedSearch ===');
      console.log(autoT('Estado actual de statusSearch:'), status);
      console.log(autoT('Estado actual de isAdvancedSearchActive:'), isAdvancedSearchActive);
      
      const params = new URLSearchParams();
      
      if (orderNum) {
        params.append('orderNumber', orderNum.trim());
      }
      
      if (status && status !== 'all') {
        params.append('status', status);
        console.log(autoT('Agregando estado a la búsqueda:'), status);
      }
      
      if (startDate) {
        params.append('startDate', startDate);
      }
      
      if (endDate) {
        params.append('endDate', endDate);
      }
      
      params.append('page', page);
      params.append('size', pageSize);
      
      const url = `purchase-orders/search?${params.toString()}`;
      console.log(autoT('URL de búsqueda:'), url);
      
      const response = await axiosInstance.get(url);
      
      if (response.data.records && response.data.records.code === "SUCCESS") {
        console.log(autoT('Búsqueda exitosa'));
        console.log(autoT('Estado actual antes de actualizar datos:'), status);
        setOrders(response.data.records.data);
        setOriginalOrders(response.data.records.data);
        // Mantener el estado actual
        console.log(autoT('Manteniendo estado actual:'), status);
        setStatusSearch(status);
      } else if (response.data.code === "SUCCESS") {
        setOrders(response.data.data);
        setOriginalOrders(response.data.data);
        setStatusSearch(status);
      } else {
        setErrorMessage(autoT('Formato de respuesta inesperado o error del servidor'));
        setShowErrorDialog(true);
      }
    } catch (err) {
      console.error(autoT('Error en búsqueda avanzada:'), err);
      setErrorMessage(autoT('Error al realizar la búsqueda:') + (err.message || autoT('Error desconocido')));
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderNumberSearchChange = (e) => {
    const newValue = e.target.value.trim();
    setOrderNumberSearch(newValue);
    handleAdvancedSearch(newValue, statusSearch, startDateSearch, endDateSearch);
  };

  const handleStatusSearchChange = (e) => {
    const newValue = e.detail.selectedOption.value;
    console.log('=== handleStatusSearchChange ===');
    console.log(autoT('Nuevo estado seleccionado:'), newValue);
    console.log(autoT('Estado actual de statusSearch:'), statusSearch);
    
    setStatusSearch(newValue);
    handleAdvancedSearch(orderNumberSearch, newValue, startDateSearch, endDateSearch);
  };

  const validateDateRange = (start, end) => {
    if (!start || !end) {
      return true;
    }
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    return endDate >= startDate;
  };

  const handleStartDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        setStartDateSearch('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newStartDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newStartDate = new Date(`${year}-${month}-${day}T00:00:00`);
        } else {
          newStartDate = new Date(dateValue);
        }
      } catch (error) {
        newStartDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newStartDate.getTime())) {
        console.error(autoT("Fecha de inicio inválida:"), dateValue);
        setErrorMessage(autoT('Fecha de inicio inválida'));
        setShowErrorDialog(true);
        return;
      }
      
      // Validación: Si hay fecha de fin, verificar que la fecha de inicio no sea posterior
      if (endDateSearch) {
        const endDate = new Date(endDateSearch);
        if (newStartDate > endDate) {
          setErrorMessage(autoT('La fecha de inicio no puede ser posterior a la fecha de fin'));
          setShowErrorDialog(true);
          return;
        }
      }
      
      const formattedDate = newStartDate.toISOString();
      setStartDateSearch(formattedDate);
      
      if (endDateSearch) {
        handleAdvancedSearch(orderNumberSearch, statusSearch, formattedDate, endDateSearch);
      }
    } catch (error) {
      console.error(autoT("Error al procesar fecha de inicio:"), error);
      setErrorMessage(autoT('Error al procesar la fecha de inicio'));
      setShowErrorDialog(true);
    }
  };

  const handleEndDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        setEndDateSearch('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newEndDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newEndDate = new Date(`${year}-${month}-${day}T23:59:59`);
        } else {
          newEndDate = new Date(dateValue);
        }
      } catch (error) {
        newEndDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newEndDate.getTime())) {
        console.error(autoT("Fecha de fin inválida:"), dateValue);
        setErrorMessage(autoT('Fecha de fin inválida'));
        setShowErrorDialog(true);
        return;
      }
      
      // Validación: Si hay fecha de inicio, verificar que la fecha de fin no sea anterior
      if (startDateSearch) {
        const startDate = new Date(startDateSearch);
        if (newEndDate < startDate) {
          setErrorMessage(autoT('La fecha de fin no puede ser anterior a la fecha de inicio'));
          setShowErrorDialog(true);
          return;
        }
      }
      
      const formattedDate = newEndDate.toISOString();
      setEndDateSearch(formattedDate);
      
      if (startDateSearch) {
        handleAdvancedSearch(orderNumberSearch, statusSearch, startDateSearch, formattedDate);
      }
    } catch (error) {
      console.error(autoT("Error al procesar fecha de fin:"), error);
      setErrorMessage(autoT('Error al procesar la fecha de fin'));
      setShowErrorDialog(true);
    }
  };

  const handleResetSearchFilters = () => {
    console.log('=== Reseteando filtros ===');
    
    // Resetear todos los estados
    setOrderNumberSearch('');
    setStatusSearch('');
    setStartDateSearch('');
    setEndDateSearch('');
    setIsAdvancedSearchActive(false);
    
    // Forzar la actualización del estado del Select
    setStatusSearch('');
    
    // Recargar los datos sin filtros
    fetchOrders(currentPage, false);
    
    console.log(autoT('Filtros reseteados, estado actual:'), {
      orderNumberSearch: '',
      statusSearch: '',
      startDateSearch: '',
      endDateSearch: '',
      isAdvancedSearchActive: false
    });
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    return date.toISOString();
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    // Solo cargar OC si la vista activa es de compra y el usuario tiene permiso
    // (los usuarios "solo OV" no deben disparar GET /purchase-orders).
    if (orderView !== 'purchase' || !canPurchase) {
      setLoading(false);
      return;
    }
    fetchOrders(currentPage);
  }, [currentPage, orderView, canPurchase]);

  const goToOrderDetail = (orderNumber) => {
    navigate(`/order-detail/${orderNumber}`);
  };

  const handleFilterStatusChange = (event) => {
    const newStatus = event.detail.selectedOption.value;
    console.log(autoT('Nuevo estado seleccionado:'), newStatus);
    setSelectedStatus(newStatus);
  
    const filteredOrders = newStatus === 'all'
      ? originalOrders
      : originalOrders.filter((order) => order.status === newStatus);
  
    console.log(autoT('Órdenes filtradas:'), filteredOrders);
  };
  


  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/upload/purchaseOrderMassive', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status === 200) {
        alert(autoT('Archivo importado exitosamente.'));
      } else {
        alert(autoT('Hubo un error al importar el archivo.'));
      }
    } catch (err) {
      console.error(autoT('Error al subir el archivo:'), err);
      setErrorMessage(autoT('Error al exportar excel: ') + (err.response.data.message || autoT('Error desconocido')));
      setShowErrorDialog(true);
    }
  };

  const handleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSendToSap = async () => {
    setSapLoading(true);
    try {
      // El backend responde al instante y envía las órdenes una por una; acá se espera el resultado.
      const result = await sendSalesOrdersToSapAndWait(selectedOrders);
      setSapResult(result);
      setShowSapConfirmDialog(false);
      setShowSapResultDialog(true);
      setSelectedOrders([]);
      fetchOrders(currentPage);
    } catch (err) {
      setErrorMessage(autoT('Error al enviar órdenes a SAP B1: ') + (err.response?.data?.message || err.message));
      setShowErrorDialog(true);
      setShowSapConfirmDialog(false);
      // El envío sigue en el servidor aunque acá se haya perdido el hilo: refrescar muestra el estado real.
      fetchOrders(currentPage);
    } finally {
      setSapLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  const areAllOrdersPending = () => {
    return selectedOrders.every(orderId => {
      const order = orders.find(o => o.id === orderId);
      return order?.status === 'Pending';
    });
  };

  const handleStatusUpdate = async () => {
    try {
        console.log(autoT('Actualizando estados de órdenes:'), selectedOrders, autoT('al estado:'), newStatus);
        
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('userData'));

        await Promise.all(selectedOrders.map(async (orderId) => {
            const statusToUpdate = newStatus === 'Shipped' ? 'Shipped' : 'Canceled';
            const requestBody = { 
              status: statusToUpdate, 
              description: newStatus === 'Canceled' && rejectionReason ? rejectionReason : null,
              modifiedBy: userData.id
            };

            await axiosInstance.put(`/purchase-orders/${orderId}`, requestBody, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
        }));

        setShowStatusDialog(false);
        setSelectedOrders([]);
        setNewStatus('');
        setRejectionReason('');
        
        await fetchOrders(currentPage);
    } catch (error) {
        console.error(autoT('Error al actualizar estados:'), error);
    }
  };


  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses en JavaScript son 0-indexados
    const year = date.getFullYear();
    return `${day}/${month}/${year}`; // Formato DD/MM/YYYY
  };


  const filterFn = (rows, accessor, filterValue) => {
    // Ensure filterValue is an array
    const filterValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    
    if (!filterValues || filterValues.length === 0) return rows;
    return rows.filter((row) => {
      const rowValue = String(row.values[accessor]).replace(/\./g, ',').toLowerCase();
      return filterValues.some((item) => 
        rowValue.includes(String(item).replace(/\./g, ',').toLowerCase())
      );
    });
  };
  


  const getUniqueClients = (orders) => {
    return [...new Set(orders.map(order => order.clientName))].sort();
  };

  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchOrders(nextPage, isAdvancedSearchActive);
  };

  const handlePreviousPage = () => {
    const prevPage = Math.max(currentPage - 1, 1);
    setCurrentPage(prevPage);
    fetchOrders(prevPage, isAdvancedSearchActive);
  };

  // Sin permiso para OC ni OV: nada que mostrar aquí.
  if (!canPurchase && !canSales) {
    return <AccessDenied />;
  }

  if (loading) return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh' 
    }}>
      <BusyIndicator active="true" size="L" text={autoT('Buscando órdenes de compra...')} />
    </div>
  );
  if (error) return <div>{autoT('Error:')} {error}</div>;

  if (showErrorDialog) return (
    <Dialog
      open={showErrorDialog}
      headerText={autoT('Error')}
      onAfterClose={() => setShowErrorDialog(false)}
    >
      <div style={{ padding: '1rem' }}>
        <Text>{errorMessage}</Text>
        <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ marginTop: '1rem' }}>
          <Button 
            onClick={() => setShowErrorDialog(false)}
            style={{
              backgroundColor: '#343a40',
              color: '#fff'
            }}
          >
            {autoT('Entendido')}
          </Button>
        </FlexBox>
      </div>
    </Dialog>
  );

  return (
    <div className="container" style={{ padding: '2rem', position: 'relative', paddingBottom: '80px' }}>
      {/* ==============================
          NUEVO (2026-03-17)
          "Tabs" (botones) OC / OV
          - En modo OV se renderiza `SalesOrderList`
          - En modo OC se mantiene el contenido existente
         ============================== */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
        {/* Fila 1: pestañas (solo se muestran las que el usuario puede ver) */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {canPurchase && (
            <Button
              design={orderView === 'purchase' ? 'Emphasized' : 'Transparent'}
              onClick={() => setOrderView('purchase')}
            >
              {autoT('Órdenes de Compra')}
            </Button>
          )}
          {canSales && (
            <Button
              design={orderView === 'sales' ? 'Emphasized' : 'Transparent'}
              onClick={() => setOrderView('sales')}
            >
              {autoT('Órdenes de Venta')}
            </Button>
          )}
        </div>

        {/* Fila 2: acciones (debajo de las pestañas, alineadas a la derecha) */}
        {orderView === 'purchase' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button
              onClick={() => fileInputRef.current.click()}
              style={{
                backgroundColor: '#2F5A88',
                color: '#F2F2F2',
                border: 'none',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px'
              }}
            >
              <Icon name="excel-attachment" style={{ marginRight: '0.5rem', color: '#F2F2F2' }} />
              {autoT('Importar Excel')}
            </Button>
            <Button
              disabled={selectedOrders.length === 0}
              onClick={() => {
                if (!areAllOrdersPending()) {
                  setShowErrorDialog(true);
                } else {
                  setShowStatusDialog(true);
                }
              }}
              style={{
                backgroundColor: selectedOrders.length === 0 ? '#ccc' : '#2F5A88',
                color: '#F2F2F2',
                border: 'none',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px'
              }}
            >
              <Icon name="process" style={{ marginRight: '0.5rem', color: '#F2F2F2' }} />
              {autoT('Procesar Órdenes Seleccionadas')} ({selectedOrders.length})
            </Button>
            <Button
              disabled={selectedOrders.length === 0 || sapLoading}
              onClick={() => setShowSapConfirmDialog(true)}
              style={{
                backgroundColor: selectedOrders.length === 0 ? '#ccc' : '#0070AD',
                color: '#F2F2F2',
                border: 'none',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px'
              }}
            >
              <Icon name="sys-enter-2" style={{ marginRight: '0.5rem', color: '#F2F2F2' }} />
              {autoT('Envío Docto a SAP')} ({selectedOrders.length})
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        )}
      </div>

      {/* ==============================
          NUEVO (2026-03-17)
          Render condicional por vista
         ============================== */}
      {orderView === 'sales' ? (
        <Suspense
          fallback={
            <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
              <BusyIndicator active size="M" />
            </div>
          }
        >
          {/* Órdenes de venta: todo el fetch y UI en `SalesOrderList`. OC arriba usa `fetchOrders` en este componente. */}
          <SalesOrderList />
        </Suspense>
      ) : (
        <>
          {/* NUEVO (2026-03-17)
              Se eliminó el título "Órdenes de compra" por redundante, pero se mantiene su espacio
              para conservar el mismo layout/aire visual. */}
          <div style={{ height: '0.5rem', marginBottom: '0rem' }} />
      
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '1rem', 
        borderRadius: '8px', 
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Title level="H4" style={{ marginBottom: '1rem' }}>{autoT('Búsqueda avanzada')}</Title>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '1rem', 
          marginBottom: '1rem' 
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Label>{autoT('Número de orden')}</Label>
            <Input 
              placeholder={autoT('Buscar por número')}
              value={orderNumberSearch}
              onChange={handleOrderNumberSearchChange}
              style={{ width: '100%' }}
            />
          </div>
          
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Label>{autoT('Estado')}</Label>
            <Select
              value={statusSearch}
              onChange={handleStatusSearchChange}
              style={{ width: '100%' }}
              selectedOption={statusSearch}
              selectedIndex={statusOptions.findIndex(opt => opt.value === statusSearch)}
              onAfterChange={(event) => {
                console.log('=== Select onAfterChange ===');
                console.log(autoT('Valor seleccionado:'), event.detail.selectedOption.value);
                console.log(autoT('Estado actual de statusSearch:'), statusSearch);
              }}
            >
              <Option value="">{autoT('Todos los estados')}</Option>
              {statusOptions.map((option) => (
                option.value !== 'all' && (
                  <Option 
                    key={option.value} 
                    value={option.value}
                    selected={option.value === statusSearch}
                  >
                    {option.label}
                  </Option>
                )
              ))}
            </Select>
          </div>
          
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Label>{autoT('Fecha de inicio')}</Label>
            <DatePicker
              value={startDateSearch ? formatDate(new Date(startDateSearch)) : ''}
              onChange={handleStartDateChange}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>
          
          <div style={{ flex: '1', minWidth: '200px' }}>
            <Label>{autoT('Fecha de fin')}</Label>
            <DatePicker
              value={endDateSearch ? formatDate(new Date(endDateSearch)) : ''}
              onChange={handleEndDateChange}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            design="Transparent" 
            onClick={handleResetSearchFilters}
          >
            {autoT('Limpiar filtros')}
          </Button>
        </div>
      </div>

      {isAdvancedSearchActive && (
        <div style={{ 
          backgroundColor: '#e6f7ff', 
          padding: '0.5rem 1rem', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Text>
            <Icon name="filter" style={{ marginRight: '0.5rem' }} />
            {autoT('Mostrando resultados de búsqueda avanzada')} {orders.length > 0 ? `(${orders.length} ${autoT('resultados')})` : `(${autoT('No se encontraron resultados')})`}
          </Text>
          <Button 
            design="Transparent" 
            onClick={handleResetSearchFilters}
            style={{ padding: '0.25rem' }}
          >
            <Icon name="decline" />
          </Button>
        </div>
      )}

      <AnalyticalTable
          data={orders}
          columns={[
          {
            Header: autoT('Seleccionar'),
            accessor: 'id',
            sortable: false,
            width: 80,
            Cell: ({ row }) => (
              <input
                type="checkbox"
                checked={selectedOrders.includes(row.original.id)}
                onChange={() => handleOrderSelection(row.original.id)}
                onClick={(e) => e.stopPropagation()}
                disabled={row.original.status !== 'Pending'}
                style={{
                  cursor: row.original.status === 'Pending' ? 'pointer' : 'not-allowed',
                  opacity: row.original.status === 'Pending' ? 1 : 0.5
                }}
              />
            )
          },
          {
            Header: autoT('Número de orden'),
            accessor: 'orderNumber',
            sortable: true,
            width: 150,
            Cell: ({ value }) => (
              <Title level="H4">{value}</Title>
            )
          },
          {
            Header: autoT('SAP DocNum'),
            accessor: 'externalId',
            width: 110,
            Cell: ({ value }) => value
              ? <ObjectStatus state="Success">{value}</ObjectStatus>
              : <Text style={{ color: '#aaa' }}>—</Text>
          },
          {
            Header: autoT('Fecha de creación'),
            accessor: 'createdAt',
            width: 140,
            Cell: ({ value }) => {
              return <Text>{formatDate(new Date(value))}</Text>;
            },
            disableFilters: true,
          },
          {
            Header: autoT('Fecha de entrega'),
            accessor: 'deliveryDate',
            width: 140,
            Cell: ({ value }) => {
              return <Text>{formatDate(new Date(value))}</Text>;
            },
            disableFilters: true,
          },
          {
            Header: autoT('Incoterm'),
            accessor: 'incoterm',
            sortable: true,
            width: 80,
            Cell: ({ value }) => (
              <Text>{value}</Text>
            )
          },
          {
            Header: autoT('Cliente'),
            accessor: 'clientName',
            sortable: true,
            width: 90,
            filter: filterFn,
            Filter: ({ column }) => (
              <Select
                onChange={(e) => {
                  const value = e.detail.selectedOption.value;
                  column.setFilter(value ? [value] : []);
                }}
              >
                <Option value="">{autoT('Todos')}</Option>
                {getUniqueClients(originalOrders).map((clientName) => (
                  <Option key={clientName} value={clientName}>
                    {clientName}
                  </Option>
                ))}
              </Select>
            ),
            Cell: ({ value }) => (
              <Text style={{ fontWeight: 'bold' }}>{value}</Text>
            )
          },
          {
            Header: autoT('RUT'),
            accessor: 'clientRut',
            sortable: true,
            width: 120,
            Cell: ({ value }) => (
              <Text>{value}</Text>
            )
          },
          {
            Header: autoT('Total'),
            accessor: 'totalAmount',
            sortable: true,
            width: 120,
            filter: filterFn,
            Cell: ({ value, row }) => {
              const { content, style } = formattedNumberCell(value, row.original.currency);
              return <Text style={style}>{content}</Text>;
            }
          },
          {
            Header: autoT('Moneda'),
            accessor: 'currency',
            sortable: true,
            width: 100,
            Cell: ({ value }) => (
              <Text>{value}</Text>
            )
          },
          {
            Header: autoT('Lugar de entrega'),
            accessor: 'clientAddress',
            sortable: true,
            Cell: ({ value }) => (
              <Text>{value}</Text>
            )
          },
          {
            Header: autoT('Estado'),
            accessor: 'status',
            sortable: true,
            width: 150,
            filter: filterFn,
            Filter: ({ column }) => (
              <Select
                onChange={(e) => {
                  const value = e.detail.selectedOption.value;
                  column.setFilter(value ? [value] : []);
                }}
              >
                <Option value="">{autoT('Todos')}</Option>
                {statusOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            ),
            Cell: ({ value }) => (
              <ObjectStatus 
                style={getStatusComponentProps(value, null, { width: '150px' }).style}
              >
                {getStatusLabel(value)}
              </ObjectStatus>
            )
          }
        ]}
        sortable={true}
        filterable={true}
        header
        scaleWidthMode="Grow"
        minRows={5}
        style={{ width: '100%' }}
        onRowClick={(event) => goToOrderDetail(event.detail.row.original.orderNumber)}
        columnBehavior={{
          header: {
            wrapHeaderText: true,
            allowMultipleLines: true
          }
        }}
      />

      {showStatusDialog && (
        <Dialog
          open={showStatusDialog}
          headerText={autoT('Cambiar Estado de Órdenes')}
          onAfterClose={() => setShowStatusDialog(false)}
        >
          <div style={{ padding: '1rem' }}>
            <Select
              value={newStatus}
              onChange={(event) => setNewStatus(event.detail.selectedOption.value)}
              style={{ width: '100%', marginBottom: '1rem' }}
            >
              <Option value="" disabled>-- {autoT('Escoja Opción')} --</Option>
              {dialogStatusOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>

            {newStatus === 'Canceled' && (
              <textarea
                placeholder={autoT('Motivo de rechazo')}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={{ width: '100%', marginBottom: '1rem', height: '100px' }}
              />
            )}

            <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ gap: '0.5rem' }}>
              <Button 
                design="Transparent" 
                onClick={() => setShowStatusDialog(false)}
              >
                {autoT('Cancelar')}
              </Button>
              <Button 
                onClick={() => {
                  if (newStatus === 'Canceled' && !rejectionReason) {
                    setErrorMessage(autoT('El motivo de rechazo no puede estar vacío.'));
                    setShowErrorDialog(true);
                    return;
                  }
                  handleStatusUpdate();
                }}
                disabled={!newStatus}
                style={{
                  backgroundColor: '#343a40',
                  color: '#fff'
                }}
              >
                {autoT('Confirmar')}
              </Button>
            </FlexBox>
          </div>
        </Dialog>
      )}

      {/* ===== SAP B1: Diálogo de confirmación ===== */}
      {showSapConfirmDialog && (
        <Dialog
          open={showSapConfirmDialog}
          headerText={autoT('Confirmar Envío a SAP B1')}
          onAfterClose={() => !sapLoading && setShowSapConfirmDialog(false)}
          footer={
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem', justifyContent: 'flex-end' }}>
              <Button
                design="Emphasized"
                onClick={handleSendToSap}
                disabled={sapLoading}
                style={{ backgroundColor: '#0070AD', color: '#fff' }}
              >
                {sapLoading ? autoT('Enviando...') : autoT('Confirmar')}
              </Button>
              <Button onClick={() => setShowSapConfirmDialog(false)} disabled={sapLoading}>
                {autoT('Cancelar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem', minWidth: '320px' }}>
            <Text>
              {autoT('¿Desea enviar')} <strong>{selectedOrders.length}</strong> {autoT('orden(es) a SAP B1 Service Layer?')}
            </Text>
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082' }}>
              <Text style={{ color: '#e65100', fontSize: '0.85rem' }}>
                {autoT('Esta acción creará Órdenes de Venta en SAP B1 para cada documento seleccionado. El DocNum quedará registrado en la columna SAP DocNum.')}
              </Text>
            </div>
          </div>
        </Dialog>
      )}

      {/* ===== SAP B1: Diálogo de resultado ===== */}
      {showSapResultDialog && (
        <Dialog
          open={showSapResultDialog}
          headerText={autoT('Resultado del Envío a SAP B1')}
          onAfterClose={() => setShowSapResultDialog(false)}
          footer={
            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button design="Emphasized" onClick={() => setShowSapResultDialog(false)}>
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem', minWidth: '360px', maxHeight: '400px', overflowY: 'auto' }}>
            {sapResult && (
              <>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <Text style={{ color: '#2e7d32' }}>✅ {sapResult.successCount} {autoT('exitosas')}</Text>
                  <Text style={{ color: '#c62828' }}>❌ {sapResult.failureCount} {autoT('con errores')}</Text>
                </div>
                {sapResult.results && sapResult.results.map((r, i) => (
                  <div key={i} style={{ padding: '0.6rem 0', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    <Text style={{ fontWeight: 'bold' }}>{r.orderNumber}</Text>
                    {r.success
                      ? <Text style={{ color: '#2e7d32', fontSize: '0.85rem' }}>SAP DocNum: <strong>{r.docNum}</strong> (DocEntry: {r.docEntry})</Text>
                      : <Text style={{ color: '#c62828', fontSize: '0.85rem' }}>{r.error}</Text>
                    }
                  </div>
                ))}
              </>
            )}
          </div>
        </Dialog>
      )}

      {showErrorDialog && (
        <Dialog
          open={showErrorDialog}
          headerText={autoT('Error')}
          onAfterClose={() => setShowErrorDialog(false)}
        >
          <div style={{ padding: '1rem' }}>
            <Text>{errorMessage}</Text>
            
            <FlexBox justifyContent={FlexBoxJustifyContent.End} style={{ marginTop: '1rem' }}>
              <Button 
                onClick={() => setShowErrorDialog(false)}
                style={{
                  backgroundColor: '#343a40',
                  color: '#fff'
                }}
              >
                {autoT('Entendido')}
              </Button>
            </FlexBox>
          </div>
        </Dialog>
      )}

      <div style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        borderTop: '1px solid #e5e5e5',
        zIndex: 100,
        width: '100%',
        paddingRight: '2rem'
      }}>
        <Button onClick={handlePreviousPage} disabled={currentPage === 1}>← {autoT('Anterior')}</Button>
        <span style={{ flex: 1, textAlign: 'center' }}>{autoT('Página')} {currentPage}</span>
        <Button onClick={handleNextPage} style={{ marginRight: '2rem' }}>{autoT('Siguiente')} →</Button>
      </div>
        </>
      )}
    </div>
  );
};

export default BuyOrderList;